import React, { useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { HistoricalNavResult } from "@/types";
import { TrendingUp } from "lucide-react";
import moment from "moment-timezone";

interface AnalyticsChartProps {
  historicalNavData: HistoricalNavResult[];
  isLoading: boolean;
}

export const AnalyticsChart: React.FC<AnalyticsChartProps> = ({
  historicalNavData,
  isLoading
}) => {
  // Process and sort the data
  const chartData = useMemo(() => {
    return historicalNavData
      .map(item => ({
        ...item,
        date: new Date(item.date),
        formattedDate: moment(item.date).format("MMM DD")
      }))
      .sort((a, b) => a.date.getTime() - b.date.getTime());
  }, [historicalNavData]);

  // Calculate chart dimensions and scales
  const chartDimensions = useMemo(() => {
    if (chartData.length < 2) return null;

    const width = 800;
    const height = 300;
    const padding = { top: 20, right: 60, bottom: 40, left: 80 };
    
    // Find min and max values for Y scale
    const values = chartData.map(d => d.value);
    const dataMinValue = Math.min(...values);
    const dataMaxValue = Math.max(...values);
    
    // Add padding around the data range (10% above and below)
    const range = dataMaxValue - dataMinValue;
    const padding_percent = 0.1;
    const minValue = Math.max(0, dataMinValue - range * padding_percent);
    const maxValue = dataMaxValue + range * padding_percent;
    
    return {
      width,
      height,
      padding,
      chartWidth: width - padding.left - padding.right,
      chartHeight: height - padding.top - padding.bottom,
      maxValue,
      minValue
    };
  }, [chartData]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(amount);
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-blue-500" />
            Historical NAV Trend
          </CardTitle>
          <CardDescription>Fund performance over time</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (chartData.length < 2 || !chartDimensions) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-blue-500" />
            Historical NAV Trend
          </CardTitle>
          <CardDescription>Fund performance over time</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            {chartData.length === 0 ? "No historical data available" : "At least 2 data points required to display trend"}
          </div>
        </CardContent>
      </Card>
    );
  }

  const { width, height, padding, chartWidth, chartHeight, maxValue, minValue } = chartDimensions;

  // Create path data for the line
  const pathData = chartData
    .map((item, index) => {
      const x = padding.left + (index / (chartData.length - 1)) * chartWidth;
      const y = padding.top + ((maxValue - item.value) / (maxValue - minValue)) * chartHeight;
      return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
    })
    .join(' ');

  // Create points for hover effects
  const points = chartData.map((item, index) => {
    const x = padding.left + (index / (chartData.length - 1)) * chartWidth;
    const y = padding.top + ((maxValue - item.value) / (maxValue - minValue)) * chartHeight;
    return { x, y, value: item.value, date: item.formattedDate };
  });

  // Y-axis ticks
  const yTicks = Array.from({ length: 6 }, (_, i) => {
    const value = minValue + (i / 5) * (maxValue - minValue);
    const y = padding.top + ((maxValue - value) / (maxValue - minValue)) * chartHeight;
    return { value, y };
  });

  // Calculate trend
  const firstValue = chartData[0]?.value || 0;
  const lastValue = chartData[chartData.length - 1]?.value || 0;
  const trend = lastValue - firstValue;
  const trendPercentage = firstValue !== 0 ? ((trend / firstValue) * 100) : 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-blue-500" />
          Historical NAV Trend
        </CardTitle>
        <CardDescription>
          Fund performance over {chartData.length} periods
          {trend !== 0 && (
            <span className={`ml-2 text-sm font-medium ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
              {trend > 0 ? '+' : ''}{formatCurrency(trend)} ({trendPercentage > 0 ? '+' : ''}{trendPercentage.toFixed(1)}%)
            </span>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="w-full overflow-x-auto h-full min-w-full ">
          <svg width={width} height={height} className="border rounded ">
            {/* Grid lines */}
            {yTicks.map((tick, index) => (
              <line
                key={index}
                x1={padding.left}
                y1={tick.y}
                x2={padding.left + chartWidth}
                y2={tick.y}
                stroke="#e2e8f0"
                strokeWidth="1"
              />
            ))}
            
            {/* Y-axis */}
            <line
              x1={padding.left}
              y1={padding.top}
              x2={padding.left}
              y2={padding.top + chartHeight}
              stroke="#64748b"
              strokeWidth="2"
            />
            
            {/* X-axis */}
            <line
              x1={padding.left}
              y1={padding.top + chartHeight}
              x2={padding.left + chartWidth}
              y2={padding.top + chartHeight}
              stroke="#64748b"
              strokeWidth="2"
            />
            
            {/* Y-axis labels */}
            {yTicks.map((tick, index) => (
              <text
                key={index}
                x={padding.left - 10}
                y={tick.y + 5}
                textAnchor="end"
                className="text-xs fill-muted-foreground"
              >
                {formatCurrency(tick.value)}
              </text>
            ))}
            
            {/* NAV trend line */}
            <path
              d={pathData}
              fill="none"
              stroke="#3b82f6"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            
            {/* Data points */}
            {points.map((point, index) => (
              <g key={index}>
                <circle
                  cx={point.x}
                  cy={point.y}
                  r="4"
                  fill="#3b82f6"
                  stroke="white"
                  strokeWidth="2"
                  className="hover:r-6 transition-all duration-200"
                />
                <title>
                  {point.date}: {formatCurrency(point.value)}
                </title>
              </g>
            ))}
            
            {/* X-axis labels */}
            {points.map((point, index) => (
              <text
                key={index}
                x={point.x}
                y={padding.top + chartHeight + 20}
                textAnchor="middle"
                className="text-xs fill-muted-foreground"
              >
                {chartData[index].formattedDate}
              </text>
            ))}
          </svg>
        </div>
        
        {/* Summary stats */}
        <div className="mt-4 grid grid-cols-3 gap-4 text-sm">
          <div className="text-center">
            <div className="font-medium text-muted-foreground">Current</div>
            <div className="text-lg font-semibold">{formatCurrency(lastValue)}</div>
          </div>
          <div className="text-center">
            <div className="font-medium text-muted-foreground">Change</div>
            <div className={`text-lg font-semibold ${trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {trend >= 0 ? '+' : ''}{formatCurrency(trend)}
            </div>
          </div>
          <div className="text-center">
            <div className="font-medium text-muted-foreground">% Change</div>
            <div className={`text-lg font-semibold ${trendPercentage >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {trendPercentage >= 0 ? '+' : ''}{trendPercentage.toFixed(1)}%
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}; 